import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/card";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Loader2, Play } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import {
  fetchSuburbProfileAdapter,
  fetchSuburbProfileRuns,
  fetchSuburbs,
  triggerSuburbCollection,
  type SuburbProfileAdapter,
  type SuburbProfileRun,
} from "@/lib/api";
import { formatDate } from "@/lib/formatDate";

const SuburbAdapterDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [testSuburbQuery, setTestSuburbQuery] = useState("");
  const [selectedSuburbId, setSelectedSuburbId] = useState<string | null>(null);

  const { data: adapter, isLoading: adapterLoading } = useQuery<SuburbProfileAdapter | null>({
    queryKey: ["suburb-profile-adapter", slug],
    queryFn: () => (slug ? fetchSuburbProfileAdapter(slug) : Promise.resolve(null)),
    enabled: !!slug,
  });

  const { data: runs, isLoading: runsLoading } = useQuery<SuburbProfileRun[]>({
    queryKey: ["suburb-profile-runs", adapter?.id],
    queryFn: () => (adapter ? fetchSuburbProfileRuns(adapter.id, 12) : Promise.resolve([])),
    enabled: !!adapter,
  });

  const { data: suburbMatchesResp } = useQuery({
    queryKey: ["suburbs-search", testSuburbQuery],
    queryFn: () => fetchSuburbs({ search: testSuburbQuery }),
    enabled: testSuburbQuery.length >= 2,
  });
  const suburbMatches = suburbMatchesResp?.data;

  const triggerMutation = useMutation({
    mutationFn: (params: { suburbIds?: string[]; force?: boolean }) =>
      triggerSuburbCollection({ ...params, runType: "manual" }),
    onSuccess: (data) => {
      toast({
        title: "Collection complete",
        description: `Run ${(data.runId ?? "—").slice(0, 8)}: ${data.done} done · ${data.failed} failed of ${data.suburbsTotal}`,
      });
      queryClient.invalidateQueries({ queryKey: ["suburb-profile-runs"] });
      queryClient.invalidateQueries({ queryKey: ["latest-suburb-snapshots"] });
    },
    onError: (err) =>
      toast({
        title: "Collection failed",
        description: (err as Error).message,
        variant: "destructive",
      }),
  });

  if (adapterLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-12">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading adapter…
      </div>
    );
  }
  if (!adapter) {
    return (
      <div className="space-y-4">
        <Link to="/dashboard/suburb-adapters" className="text-sm text-muted-foreground hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to suburb adapters
        </Link>
        <p className="text-destructive">Adapter not found.</p>
      </div>
    );
  }

  const latestRun = runs?.[0];

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/dashboard/suburb-adapters"
          className="text-sm text-muted-foreground hover:underline inline-flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="w-4 h-4" /> Suburb adapters
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">{adapter.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">{adapter.description}</p>
          </div>
          <Badge variant="outline">{adapter.status}</Badge>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="runs">Run history</TabsTrigger>
          <TabsTrigger value="sample">Test scrape</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Base URL</Label>
                  <p>
                    <a
                      href={adapter.base_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline inline-flex items-center gap-1"
                    >
                      {adapter.base_url} <ExternalLink className="w-3 h-3" />
                    </a>
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Slug</Label>
                  <p><code>{adapter.slug}</code></p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Created</Label>
                  <p>{formatDate(adapter.created_at)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Updated</Label>
                  <p>{formatDate(adapter.updated_at)}</p>
                </div>
              </div>
              {Object.keys(adapter.default_config).length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">Default config</Label>
                  <pre className="text-xs bg-muted p-3 rounded mt-1 overflow-x-auto">
                    {JSON.stringify(adapter.default_config, null, 2)}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Latest run</CardTitle>
              <CardDescription>Most recent collection sweep across all suburbs.</CardDescription>
            </CardHeader>
            <CardContent>
              {!latestRun ? (
                <p className="text-sm text-muted-foreground">No runs yet — kick one off from the Test scrape tab.</p>
              ) : (
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <p><Badge variant="outline">{latestRun.status}</Badge></p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Started</Label>
                    <p>{formatDate(latestRun.started_at)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Suburbs</Label>
                    <p>{latestRun.suburbs_done} / {latestRun.suburbs_total} done · {latestRun.suburbs_failed} failed</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Type</Label>
                    <p>{latestRun.run_type}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Run now</CardTitle>
              <CardDescription>
                Trigger a collection across every suburb in the database. Each suburb that already
                has a snapshot for the current month is skipped.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => triggerMutation.mutate({})}
                disabled={triggerMutation.isPending}
              >
                {triggerMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Run all suburbs now
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="runs" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {runsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading runs…
                </div>
              ) : !runs || runs.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6">No runs yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Started</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Done</TableHead>
                      <TableHead>Failed</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Completed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runs.map((run) => (
                      <TableRow key={run.id}>
                        <TableCell className="text-xs">{formatDate(run.started_at)}</TableCell>
                        <TableCell>{run.run_type}</TableCell>
                        <TableCell><Badge variant="outline">{run.status}</Badge></TableCell>
                        <TableCell>{run.suburbs_done}</TableCell>
                        <TableCell>{run.suburbs_failed}</TableCell>
                        <TableCell>{run.suburbs_total}</TableCell>
                        <TableCell className="text-xs">
                          {run.completed_at ? formatDate(run.completed_at) : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sample" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Test scrape for one suburb</CardTitle>
              <CardDescription>
                Pick a suburb to verify the URL pattern, parser, and provider cascade end-to-end.
                Each test costs roughly one provider scrape (~$0.005 via Brightdata).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Search suburb</Label>
                <Input
                  type="text"
                  placeholder="Type at least 2 characters…"
                  value={testSuburbQuery}
                  onChange={(e) => setTestSuburbQuery(e.target.value)}
                />
              </div>
              {suburbMatches && suburbMatches.length > 0 && (
                <div className="border rounded-md max-h-56 overflow-y-auto divide-y">
                  {suburbMatches.slice(0, 20).map((s: { id: string; suburb_name: string; state: string; postcode: string }) => (
                    <button
                      type="button"
                      key={s.id}
                      onClick={() => setSelectedSuburbId(s.id)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-muted ${
                        selectedSuburbId === s.id ? "bg-muted" : ""
                      }`}
                    >
                      {s.suburb_name}, {s.state} {s.postcode}
                    </button>
                  ))}
                </div>
              )}
              <Button
                disabled={!selectedSuburbId || triggerMutation.isPending}
                onClick={() =>
                  triggerMutation.mutate({
                    suburbIds: selectedSuburbId ? [selectedSuburbId] : undefined,
                    force: true,
                  })
                }
              >
                {triggerMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Test scrape (~$0.005)
              </Button>
              {triggerMutation.data && (
                <div className="text-xs bg-muted p-3 rounded">
                  <pre className="overflow-x-auto">
                    {JSON.stringify(triggerMutation.data, null, 2)}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SuburbAdapterDetail;
